export type DayPatternEntry = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  employeeIds: string[];
};

/**
 * One problem with one entry of the pattern, so the card for that entry can
 * show it against the control that is at fault.
 *
 * `index` is the entry's position in the pattern the problem was found in, and
 * is what identifies the entry - `dayOfWeek` does not, because a pattern loaded
 * from the API can hold the same weekday twice. Matching on the weekday alone
 * showed each copy's problems on both copies' cards, so a card could assert
 * "Add at least one employee." about itself while holding a crew.
 *
 * `field` names the control the message is about, so the card can attach it to
 * that input with aria-describedby instead of leaving a screen reader to pair
 * them up by reading ahead. "dayOfWeek" means no single control is at fault -
 * the fix is to drop the whole card - and the message belongs to the card.
 */
export type InvalidPatternDay = {
  index: number;
  dayOfWeek: number;
  day: string;
  field: keyof DayPatternEntry;
  reason: string;
};

export const WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

/**
 * Picks the setup a newly selected day should inherit: the nearest already
 * configured day *before* it, falling back to the nearest one after it. Filling
 * a week in left to right, each new day then continues the one beside it, and
 * the result depends only on the weekdays themselves - no record of the order
 * the user clicked the chips in. With Tue and Sat configured, a new Thu copies
 * Tuesday, and a new Sunday copies Tuesday as the nearest following day.
 */
const templateFor = (
  sortedPattern: DayPatternEntry[],
  dayOfWeek: number
): DayPatternEntry | undefined => {
  const preceding = sortedPattern.filter((day) => day.dayOfWeek < dayOfWeek).pop();

  return preceding ?? sortedPattern.find((day) => day.dayOfWeek > dayOfWeek);
};

/**
 * Keeps the per-day cards in step with the weekday chips. A newly selected day
 * inherits its neighbour's setup (see templateFor), so the common case -
 * several identical days and one that differs - is "pick the days, change the
 * odd one out" rather than filling in every card from scratch.
 */
export const syncPatternWithDays = (
  pattern: DayPatternEntry[],
  selectedDays: number[],
  jobStartTime: string,
  jobEndTime: string
): DayPatternEntry[] => {
  const existing = new Map(pattern.map((day) => [day.dayOfWeek, day]));
  // Sorted here rather than trusted: the caller's array is normally already in
  // weekday order, but templateFor's "nearest" only holds if it really is.
  const sortedPattern = [...pattern].sort((a, b) => a.dayOfWeek - b.dayOfWeek);

  // Deduped: the backend rejects a pattern with two entries for one weekday,
  // and a repeated chip would otherwise emit the same day object twice.
  return Array.from(new Set(selectedDays))
    .sort((a, b) => a - b)
    .map((dayOfWeek) => {
      const inherited = existing.get(dayOfWeek);

      if (inherited) {
        return inherited;
      }

      const template = templateFor(sortedPattern, dayOfWeek);

      return {
        dayOfWeek,
        startTime: template?.startTime ?? jobStartTime,
        endTime: template?.endTime ?? jobEndTime,
        // Copied, not shared - two cards editing one array would move
        // employees between days.
        employeeIds: [...(template?.employeeIds ?? [])],
      };
    });
};

/**
 * Pattern entries hold "HH:mm", which is what the card's native
 * <input type="time"> emits directly (the form's own startTime/endTime are
 * still `Date | null`). A time loaded back from the Postgres `time` column
 * reads as "HH:mm:ss", and lexicographically "17:00" < "17:00:00".
 * Never compare the two raw - cut both to minute precision first.
 *
 * Minute precision, not seconds, because that is all the backend accepts: the
 * DTO matches /^([01]\d|2[0-3]):[0-5]\d$/ on both times and the expansion
 * discards seconds anyway. The form wiring must run DB-loaded times through
 * this before they enter a pattern, or the DTO rejects them outright.
 */
export const toMinutePrecision = (time: string): string => time.slice(0, 5);

/**
 * The day-level rejections the user can fix on the card, caught before submit
 * so the message lands on that card instead of coming back as a server error.
 * The DTO rejects more than this - a non-integer or out-of-range dayOfWeek,
 * non-string employee ids, a bad time zone - but none of those are reachable
 * from the chips and inputs the card offers.
 *
 * A blank time is reachable, though. The card uses a native
 * <input type="time">, which emits "" when cleared or partly typed; there is
 * no Date to format and no picker to intercept it, so the emptiness has to be
 * caught here or it reaches the DTO as a 400.
 *
 * One entry per problem, so a day with two faults reports both. The exception
 * is a repeated weekday: it reports only the repeat, since the fix is to drop
 * the duplicate card rather than to correct anything on it.
 */
export const describeInvalidPatternDays = (pattern: DayPatternEntry[]): InvalidPatternDay[] => {
  const problems: InvalidPatternDay[] = [];
  const seen = new Set<number>();

  pattern.forEach((entry, index) => {
    const day = WEEKDAY_NAMES[entry.dayOfWeek];

    if (seen.has(entry.dayOfWeek)) {
      // The DTO rejects a repeated weekday - flag it here so the message
      // lands on the card instead of coming back as a 400. It lands on the
      // later copy: the earlier one is the entry the user configured first and
      // is keeping, so blaming it would point at the wrong card.
      problems.push({
        index,
        dayOfWeek: entry.dayOfWeek,
        day,
        field: "dayOfWeek",
        reason: "This weekday is already configured.",
      });
      return;
    }
    seen.add(entry.dayOfWeek);

    // A cleared or half-typed native time input emits "", which the DTO's
    // /^([01]\d|2[0-3]):[0-5]\d$/ rejects outright.
    if (!entry.startTime) {
      problems.push({
        index,
        dayOfWeek: entry.dayOfWeek,
        day,
        field: "startTime",
        reason: "Add a start time.",
      });
    }

    if (!entry.endTime) {
      problems.push({
        index,
        dayOfWeek: entry.dayOfWeek,
        day,
        field: "endTime",
        reason: "Add an end time.",
      });
    }

    // Equal times would be read as an overnight shift and expand to 24 hours.
    // Gated on both being set: two blank times are equal to each other, so an
    // ungated check told a user with nothing filled in that the times must
    // differ, instead of asking for the two missing times.
    if (
      entry.startTime &&
      entry.endTime &&
      toMinutePrecision(entry.startTime) === toMinutePrecision(entry.endTime)
    ) {
      problems.push({
        index,
        dayOfWeek: entry.dayOfWeek,
        day,
        // Blamed on the end time, because that is the one the message asks the
        // user to change.
        field: "endTime",
        reason: "End time must differ from start time.",
      });
    }

    if (entry.employeeIds.length === 0) {
      problems.push({
        index,
        dayOfWeek: entry.dayOfWeek,
        day,
        field: "employeeIds",
        reason: "Add at least one employee.",
      });
    }
  });

  return problems;
};
